import type { Assignment, Course } from "../data/models";
import { useServices } from "../data/services";
import { useAsync } from "../data/useAsync";
import { dueIn, relativeShort } from "../data/format";
import { initials } from "../data/assets";
import { useNav } from "../nav/router";
import { Screen } from "../nav/Shell";
import { Avatar, Button, Card, CourseBadge, Pill } from "../components/primitives";
import { EmptyState, ErrorState, Skeleton } from "../components/states";
import "./study.css";

/**
 * Study — the course list and the GPA hero.
 *
 * The course badge here is the same component the Learn feed renders. That
 * sameness is the deliverable: a data linkage nobody can see proves nothing, so
 * "CS 101" must be visibly identical in both places.
 */
export function StudyTab() {
  const { academic } = useServices();
  const { push } = useNav();

  const courses = useAsync(() => academic.getCourses(), []);
  // The real GPA, not a percentage rescaled — the service already keeps one.
  const gpa = useAsync(() => academic.getOverallGPA(), []);
  const assignments = useAsync(() => academic.getAssignments(), []);

  return (
    <Screen>
      <GpaHero gpa={gpa.data} loading={gpa.status === "loading"} failed={gpa.status === "error"} />

      {courses.status === "loading" ? (
        <div className="stack">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={62} radius={16} />
          ))}
        </div>
      ) : courses.status === "error" ? (
        <ErrorState
          title="Couldn’t load your courses"
          body="Your schedule is saved — this is just the connection."
          onRetry={courses.reload}
        />
      ) : courses.data.length === 0 ? (
        <EmptyState
          icon="fa-graduation-cap"
          title="No courses yet"
          body="Add your schedule and Freshman starts tracking deadlines, grades, and matching course content."
          action={{ label: "Import from schedule photo", onPress: () => {} }}
        />
      ) : (
        <div className="stack">
          {courses.data.map((course) => (
            <CourseRow
              key={course.id}
              course={course}
              assignments={assignments.data ?? []}
              onOpen={() => push({ name: "courseDetail", courseId: course.id })}
            />
          ))}
          <button type="button" className="add-course">
            <i className="fa-solid fa-plus" aria-hidden="true" /> Add a course
          </button>
        </div>
      )}
    </Screen>
  );
}

/**
 * Flat brand-tint fill rather than a gradient (rule D2) — gradients band badly
 * on a dark canvas at this size.
 */
function GpaHero({ gpa, loading, failed }: { gpa?: number; loading: boolean; failed: boolean }) {
  return (
    <div className="gpa-hero">
      <div className="t-micro" style={{ color: "var(--brand-tint-text)" }}>
        Projected GPA · Fall
      </div>
      {loading ? (
        <Skeleton height={34} width={72} radius={8} />
      ) : (
        <div className="gpa-hero__value">{gpa !== undefined ? gpa.toFixed(2) : "—"}</div>
      )}

      {/* A sync failure keeps the last-known numbers and says so. A blank
          dashboard reads as "your grades are gone", which is never true. */}
      {failed ? (
        <Pill tone="reward">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /> Synced earlier
        </Pill>
      ) : (
        <div className="gpa-hero__delta">
          <i className="fa-solid fa-arrow-up" aria-hidden="true" /> 0.2 vs last check
        </div>
      )}
    </div>
  );
}

function CourseRow({
  course,
  assignments,
  onOpen,
}: {
  course: Course;
  assignments: Assignment[];
  onOpen: () => void;
}) {
  const open = assignments.filter(
    (a) => a.courseId === course.id && a.status !== "graded" && a.status !== "submitted",
  );
  const next = open[0];

  return (
    <Card compact onClick={onOpen}>
      <div className="course-row">
        <CourseBadge code={course.code ?? course.name} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="course-row__name">{course.name}</div>
          <div className="course-row__meta">
            {course.professor?.displayName ?? "Unassigned"}
            {next ? (
              <>
                {" · "}
                <span className={open.length > 1 ? "course-row__urgent" : undefined}>
                  {open.length > 1 ? `${open.length} due this week` : dueIn(next.dueDate)}
                </span>
              </>
            ) : (
              " · all caught up"
            )}
          </div>
        </div>
        <i className="fa-solid fa-chevron-right course-row__chevron" aria-hidden="true" />
      </div>
    </Card>
  );
}

/* ---------------- Course detail ---------------- */

export function CourseDetail({ courseId }: { courseId: string }) {
  const { academic } = useServices();
  const { push } = useNav();

  const courses = useAsync(() => academic.getCourses(), []);
  const assignments = useAsync(() => academic.getAssignments(courseId), [courseId]);
  const groups = useAsync(() => academic.getStudyGroups(courseId), [courseId]);
  const announcements = useAsync(() => academic.getCourseAnnouncements(courseId), [courseId]);
  const projection = useAsync(() => academic.getGradeProjection(courseId), [courseId]);

  const course = courses.data?.find((c) => c.id === courseId);

  if (courses.status === "error") {
    return (
      <Screen>
        <ErrorState onRetry={courses.reload} />
      </Screen>
    );
  }

  return (
    <Screen>
      <div className="detail-head">
        <CourseBadge code={course?.code ?? ""} />
        <h1 className="t-title">{course?.name ?? <Skeleton height={22} width="70%" />}</h1>
      </div>

      {course?.professor ? (
        <Card
          compact
          onClick={() => push({ name: "professor", professorId: course.professor!.id })}
        >
          <div className="prof-row">
            {/* Professors are initials-only by policy, not omission: no rights
                to faculty photos, and a synthetic face on a named professor
                reads uncanny where student avatars don't. */}
            <span className="prof-row__initials">{initials(course.professor.displayName)}</span>
            <div style={{ flex: 1 }}>
              <div className="course-row__name">{course.professor.displayName}</div>
              <div className="course-row__meta">
                {course.professor.averageRating?.toFixed(1)}
                <i
                  className="fa-solid fa-star"
                  style={{ color: "var(--warning)", fontSize: 10, margin: "0 4px" }}
                  aria-hidden="true"
                />
                · {course.professor.ratingCount} reviews
              </div>
            </div>
            <i className="fa-solid fa-chevron-right course-row__chevron" aria-hidden="true" />
          </div>
        </Card>
      ) : null}

      {projection.data ? (
        <div className="gpa-hero" style={{ marginTop: "var(--space-3)" }}>
          <div className="t-micro" style={{ color: "var(--brand-tint-text)" }}>
            Grade projection
          </div>
          <div className="gpa-hero__value">{Math.round(projection.data.currentGrade)}%</div>
          <div className="gpa-hero__delta">on track for A−</div>
        </div>
      ) : null}

      <SectionHeader title="Due next" />
      {assignments.status === "loading" ? (
        <Skeleton height={54} radius={12} />
      ) : assignments.data && assignments.data.length > 0 ? (
        <div className="stack">
          {assignments.data
            .filter((a) => a.status !== "graded")
            .slice(0, 3)
            .map((a) => (
              <Card key={a.id} compact>
                <div className="course-row">
                  <div style={{ flex: 1 }}>
                    <div className="course-row__name">{a.title}</div>
                    {a.description ? <div className="course-row__meta">{a.description}</div> : null}
                  </div>
                  <Pill tone={a.status === "overdue" ? "urgent" : "meta"}>{dueIn(a.dueDate)}</Pill>
                </div>
              </Card>
            ))}
        </div>
      ) : (
        <EmptyState title="Nothing due" body="You’re caught up in this course." />
      )}

      {groups.data && groups.data.length > 0 ? (
        <>
          <SectionHeader title="Study group" />
          {groups.data.map((g) => (
            <Card key={g.id} compact>
              <div className="card__body">
                <div className="course-row__name">{g.name}</div>
                <div className="course-row__meta">{g.memberCount} peers</div>
                {g.groupChatId ? (
                  <Button kind="secondary" block onClick={() => {}}>
                    Open group chat in Telegram
                  </Button>
                ) : null}
              </div>
            </Card>
          ))}
        </>
      ) : null}

      {announcements.data && announcements.data.length > 0 ? (
        <>
          <SectionHeader title="Announcements" />
          <div className="stack">
            {announcements.data.map((a) => (
              <Card key={a.id} compact>
                <div className="course-row">
                  <span style={{ fontSize: 18 }}>{a.emoji}</span>
                  <div style={{ flex: 1 }}>
                    <div className="course-row__name">{a.title}</div>
                    <div className="course-row__meta">{relativeShort(a.postedAt)}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      ) : null}
    </Screen>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="section-header">
      <span className="t-micro" style={{ color: "var(--text-hint)" }}>
        {title}
      </span>
    </div>
  );
}

/* ---------------- Professor profile ---------------- */

export function ProfessorScreen({ professorId }: { professorId: string }) {
  const { professor: professorService } = useServices();
  const professor = useAsync(() => professorService.getProfessor(professorId), [professorId]);
  const reviews = useAsync(() => professorService.getReviews(professorId), [professorId]);

  if (professor.status === "error") {
    return (
      <Screen>
        <ErrorState onRetry={professor.reload} />
      </Screen>
    );
  }

  return (
    <Screen>
      <div className="prof-head">
        <span className="prof-row__initials prof-head__initials">
          {professor.data ? initials(professor.data.displayName) : ""}
        </span>
        <div>
          <h1 className="t-title">
            {professor.data?.displayName ?? <Skeleton height={22} width={160} />}
          </h1>
          <div className="course-row__meta">{professor.data?.department}</div>
        </div>
      </div>

      {professor.data ? (
        <Card compact>
          <div className="card__body">
            <div className="course-row__meta">
              <i
                className="fa-solid fa-star"
                style={{ color: "var(--warning)" }}
                aria-hidden="true"
              />{" "}
              <strong style={{ color: "var(--text)" }}>
                {professor.data.averageRating?.toFixed(1)}
              </strong>{" "}
              · {professor.data.ratingCount} reviews
            </div>
            {professor.data.officeLocation ? (
              <div className="course-row__meta">
                <i className="fa-solid fa-location-dot" aria-hidden="true" />{" "}
                {professor.data.officeLocation}
              </div>
            ) : null}
            {professor.data.officeHours ? (
              <div className="course-row__meta">
                <i className="fa-regular fa-clock" aria-hidden="true" /> Office hours{" "}
                {professor.data.officeHours}
              </div>
            ) : null}
          </div>
        </Card>
      ) : null}

      <SectionHeader title="Reviews" />
      {reviews.status === "loading" ? (
        <Skeleton height={140} radius={12} />
      ) : (
        <div className="stack">
          {(reviews.data ?? []).map((review) => (
            <Card key={review.id} compact>
              <div className="card__body">
                <div className="card__attribution">
                  {/* Outside reviewers have no photos — this list is the seed's
                      real exercise of the initials-on-pastel fallback. */}
                  <Avatar id={review.author.id} name={review.author.displayName} size={24} />
                  <div style={{ flex: 1 }}>
                    <div className="card__author">{review.author.displayName}</div>
                    <div className="card__meta">{relativeShort(review.createdAt)}</div>
                  </div>
                  <span className="prof-stars" aria-label={`${review.rating} out of 5`}>
                    {Array.from({ length: 5 }, (_, i) => (
                      <i
                        key={i}
                        className={i < review.rating ? "fa-solid fa-star" : "fa-regular fa-star"}
                        aria-hidden="true"
                      />
                    ))}
                  </span>
                </div>
                {review.text ? <div className="card__content">{review.text}</div> : null}
              </div>
            </Card>
          ))}
        </div>
      )}
    </Screen>
  );
}
